import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import EducatorQuestion from '../EducatorQuestion/EducatorQuestion';
import { getTokenForRequest } from '../../utils/apiAuth';
import './StudentReviewModal.css';
import ReviewQuestionDisplay from './ReviewQuestionDisplay';

// Global axios interceptor for 401 Unauthorized
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      window.location.href = '/login.aspx';
      return;
    }
    return Promise.reject(error);
  }
);

/**
 * Helper function to encode token for API calls
 * @param {string} token - The session token
 * @returns {string} - Encoded token
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * StudentReviewModal Component
 * Displays a modal with review questions for a student's topic progress
 */
const StudentReviewModal = ({ isOpen, onClose, topicId, studentId, msID, topicName, studentName }) => {
  const [reviewData, setReviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentReviewQuestionId, setCurrentReviewQuestionId] = useState(null);
  const [currentReviewQuestion, setCurrentReviewQuestion] = useState(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);

  useEffect(() => {
    if (isOpen && topicId && studentId) {
      fetchReviewData();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Reset state when modal closes
      setReviewData(null);
      setCurrentQuestionIndex(0);
      setCurrentReviewQuestionId(null);
      setError(null);
      // Restore body scroll
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, topicId, studentId, msID]);

  /**
   * Fetches review data from the API
   */
  const fetchReviewData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const tokenForRequest = getTokenForRequest();

      const response = await axios.post('/webservices/Test.svc/genreateReview', {
        topicID: topicId,
        studentID: studentId,
        type: 0, // 0 for educator review
        token: encodeToken(tokenForRequest),
        moduleSemesterID: msID || 0
      });

      if (response.data) {
        setReviewData(response.data);
        // Set the first question
        if (response.data.QuestionIDs && response.data.QuestionIDs.length > 0) {
          const firstReviewQuestionId = response.data.QuestionIDs[0];
          setCurrentReviewQuestionId(firstReviewQuestionId);
          setCurrentQuestionIndex(0);
          // Fetch the first review question
          fetchReviewQuestion(firstReviewQuestionId);
        }
      }
    } catch (error) {
      console.error('Error fetching review data:', error);
      setError('Failed to load review questions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches a review question by reviewQuestionID
   */
  const fetchReviewQuestion = async (reviewQuestionID) => {
    if (!reviewQuestionID) return;

    try {
      setIsLoadingQuestion(true);
      const tokenForRequest = getTokenForRequest();

      const response = await axios.get('/webservices/Test.svc/reviewQuestion', {
        params: {
          token: encodeToken(tokenForRequest),
          reviewQuestionID: reviewQuestionID
        },
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data) {
        setCurrentReviewQuestion(response.data);
      }
    } catch (error) {
      console.error('Error fetching review question:', error);
      setCurrentReviewQuestion(null);
    } finally {
      setIsLoadingQuestion(false);
    }
  };

  /**
   * Handles clicking on a question item
   */
  const handleQuestionClick = (questionIndex) => {
    if (reviewData && reviewData.QuestionIDs && reviewData.QuestionIDs[questionIndex]) {
      const reviewQuestionId = reviewData.QuestionIDs[questionIndex];
      setCurrentQuestionIndex(questionIndex);
      setCurrentReviewQuestionId(reviewQuestionId);
      // Fetch the review question data
      fetchReviewQuestion(reviewQuestionId);
    }
  };

  /**
   * Gets the question status (correct/incorrect)
   */
  const getQuestionStatus = (index) => {
    if (!reviewData || !reviewData.QuestionCorrectData) return null;
    const status = reviewData.QuestionCorrectData[index];
    return status === 1 || status === '1' ? 'correct' : status === 0 || status === '0' ? 'incorrect' : null;
  };

  /**
   * Gets the question level class
   */
  const getQuestionLevelClass = (index) => {
    if (!reviewData || !reviewData.QuestionLevels) return '';
    const level = reviewData.QuestionLevels[index];
    return `level-${level}`;
  };

  /**
   * Gets question statistics by level
   */
  const getQuestionStats = () => {
    if (!reviewData || !reviewData.QuestionLevels) return [0, 0, 0, 0];
    const stats = [0, 0, 0, 0];
    reviewData.QuestionLevels.forEach(level => {
      if (level >= 1 && level <= 4) {
        stats[level - 1]++;
      }
    });
    return stats;
  };

  /**
   * Gets the level name
   */
  const getLevelName = (level) => {
    const levelNames = {
      1: 'Beginner',
      2: 'Advanced Beginner',
      3: 'Competent',
      4: 'Expert'
    };
    return levelNames[level] || 'Unknown';
  };

  /**
   * Handles next question navigation
   */
  const handleNextQuestion = () => {
    if (reviewData && currentQuestionIndex < reviewData.QuestionIDs.length - 1) {
      handleQuestionClick(currentQuestionIndex + 1);
    }
  };

  /**
   * Handles previous question navigation
   */
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      handleQuestionClick(currentQuestionIndex - 1);
    }
  };

  if (!isOpen) return null;

  const token = getTokenForRequest();

  const modalContent = (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show student-review-backdrop" 
        style={{ zIndex: 99998 }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block student-review-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 99999 }}
      >
        <div className="modal-dialog modal-xl" role="document" style={{ margin: '6rem auto 1rem', position: 'relative', zIndex: 1 }}>
          <div className="modal-content" style={{ maxHeight: 'calc(100vh - 7rem)', height: 'calc(100vh - 7rem)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            <div className="modal-header" style={{ position: 'relative', flexShrink: 0 }}>
              <h5 className="modal-title" style={{ fontSize: '18px', fontWeight: 'bold', textAlign: 'center', width: '100%', margin: 0, paddingRight: '30px' }}>
                Review about {topicName || 'Topic'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px' }}
              ></button>
            </div>

            <div className="modal-body" style={{ flex: '0 0 auto', minHeight: 0, maxHeight: 'calc(100vh - 11rem)', height: 'calc(100vh - 11rem)', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
              {isLoading ? (
                <div className="text-center" style={{ padding: '40px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-secondary">Loading review questions...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              ) : reviewData && reviewData.QuestionIDs && reviewData.QuestionIDs.length > 0 ? (
                <div className="student-review-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                  {/* Top Bar with Question Level, Difficulty Level Stats, and Score Card */}
                  <div style={{ backgroundColor: 'darkslategray', padding: '8px 12px', margin: '0 5px 5px 5px', borderRadius: '4px', flexShrink: 0 }}>
                    <div className="topBar">
                      {/* Left Side - Question Level and Difficulty Stats */}
                      <div className="d-flex flex-column flex-shrink-0">
                        <div className="diagnosing-info">
                          <span style={{ marginRight: '10px', fontWeight: 'bold', fontSize: '14px', color: '#fff' }}>Question Level :</span>
                          {currentReviewQuestion && currentReviewQuestion.Question && (
                            <span className="review-question-level" style={{ color: '#fff' }}>
                              {getLevelName(currentReviewQuestion.Question.Difficulty)}
                            </span>
                          )}
                        </div>
                        <div className="question-stats">
                          {(() => {
                            const stats = getQuestionStats();
                            return stats.map((count, idx) => (
                              <span
                                key={idx}
                                className={`level-${idx + 1}`}
                              >
                                {count}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>

                      {/* Right Side - Score Card */}
                      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '3px', color: '#fff', fontSize: '13px', textAlign: 'right' }}>Score Card</div>
                        <div className="scoreItems">
                          {reviewData.QuestionIDs.map((qId, index) => {
                            const status = getQuestionStatus(index);
                            const level = reviewData.QuestionLevels ? reviewData.QuestionLevels[index] : 1;
                            const isActive = index === currentQuestionIndex;

                            return (
                              <div
                                key={index}
                                className={`scoreItem scoreItemLevel-${level} ${isActive ? 'active-score-card' : ''}`}
                                onClick={() => handleQuestionClick(index)}
                                title={`Question ${index + 1} - ${status === 'correct' ? 'Correct' : 'Incorrect'}`}
                                style={{
                                  color: status === 'correct' ? '#ffffff' : status === 'incorrect' ? '#ffffff' : '#ffffff',
                                  textShadow: status === 'correct' 
                                    ? '0 1px 3px rgba(0, 0, 0, 0.7), 0 0 2px rgba(40, 167, 69, 0.8)' 
                                    : status === 'incorrect' 
                                    ? '0 1px 3px rgba(0, 0, 0, 0.7), 0 0 2px rgba(220, 53, 69, 0.8)' 
                                    : '0 1px 2px rgba(0, 0, 0, 0.5)',
                                  fontWeight: 'bold'
                                }}
                              >
                                {status === 'correct' ? '✓' : status === 'incorrect' ? '✗' : ''}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Area - Question Display */}
                  <div style={{ flex: '1 1 0%', minHeight: 0, padding: '0 5px', overflow: 'auto', backgroundColor: '#fff', borderRadius: '4px', margin: '0 5px 5px 5px', display: 'flex', flexDirection: 'column' }}>
                    {isLoadingQuestion ? (
                      <div className="text-center" style={{ padding: '40px' }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading question...</span>
                        </div>
                        <p style={{ marginTop: '10px' }}>Loading question...</p>
                      </div>
                    ) : currentReviewQuestion ? (
                      <div className="question-display" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <ReviewQuestionDisplay
                          reviewQuestion={currentReviewQuestion}
                          token={token}
                          onNextQuestion={handleNextQuestion}
                          canGoNext={reviewData && currentQuestionIndex < reviewData.QuestionIDs.length - 1}
                          isLastQuestion={reviewData && currentQuestionIndex >= reviewData.QuestionIDs.length - 1}
                          isCorrect={getQuestionStatus(currentQuestionIndex) === 'correct'}
                        />
                      </div>
                    ) : currentReviewQuestionId ? (
                      <div className="alert alert-warning" role="alert">
                        Failed to load question data.
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="alert alert-info" role="alert">
                  No questions found for this review.
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );

  // Render modal using Portal to document.body to avoid stacking context issues
  return createPortal(modalContent, document.body);
};

export default StudentReviewModal;

